/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\VoiceGuideCompletionModal.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Voice Guides module.
----------------------------------------------------------------*/


import { CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';

interface CompletionState {
  show: boolean;
  success: boolean;
  generated: number;
  skipped: number;
  failed: number;
  total: number;
  errors: string[];
}

interface VoiceGuideCompletionModalProps {
  completionModal: CompletionState;
  onGenerateMore: () => void;
  onClose: () => void;
  t: (key: string, fallback?: string) => string;
}

export function VoiceGuideCompletionModal({
  completionModal, onGenerateMore, onClose, t,
}: VoiceGuideCompletionModalProps) {
  if (!completionModal.show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card rounded-lg shadow-xl p-6 max-w-md w-full mx-4 border border-border">
        <div className="text-center">
          {/* Icon */}
          {completionModal.success ? (
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400" />
            </div>
          ) : (
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <AlertCircle className="h-10 w-10 text-amber-600 dark:text-amber-400" />
            </div>
          )}

          {/* Title */}
          <h3 className="text-lg font-semibold mb-2">
            {completionModal.success
              ? (t('voiceGuides:form.generationComplete') || 'Generation Complete!')
              : (t('voiceGuides:form.generationPartial') || 'Generation Completed with Errors')}
          </h3>

          {/* Summary */}
          <div className="text-sm text-muted-foreground mb-4 space-y-1">
            <p>
              <span className="text-green-600 dark:text-green-400 font-medium">{completionModal.generated}</span>{' '}
              {t('voiceGuides:form.generated') || 'generated'}
            </p>
            {completionModal.skipped > 0 && (
              <p>
                <span className="text-blue-600 dark:text-blue-400 font-medium">{completionModal.skipped}</span>{' '}
                {t('voiceGuides:form.skipped') || 'skipped (already existed)'}
              </p>
            )}
            {completionModal.failed > 0 && (
              <p>
                <span className="text-red-600 dark:text-red-400 font-medium">{completionModal.failed}</span>{' '}
                {t('voiceGuides:form.failedCount') || 'failed'}
              </p>
            )}
            <p className="text-xs text-muted-foreground/70 pt-1">
              {t('voiceGuides:form.totalOf') || 'Total:'} {completionModal.total} {t('voiceGuides:form.prompts') || 'prompts'}
            </p>
          </div>

          {/* Error list */}
          {completionModal.errors.length > 0 && (
            <div className="mb-4 text-left">
              <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-1">
                {t('voiceGuides:form.failedFiles') || 'Failed files:'}
              </p>
              <div className="max-h-32 overflow-y-auto bg-muted/50 rounded border border-border p-2 text-xs font-mono">
                {completionModal.errors.map((err, idx) => (
                  <div key={idx} className="text-red-600 dark:text-red-400 truncate" title={err}>
                    {err}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={onGenerateMore}>
              {t('voiceGuides:form.stayOnPage') || 'Generate More'}
            </Button>
            <Button onClick={onClose}>
              {t('voiceGuides:form.backToList') || 'Back to List'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
